import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[8px] text-sm font-semibold tracking-[-0.005em] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary CTA - pink accent (solid for WCAG contrast)
        default:
          "bg-accent-strong text-white shadow-pill hover:bg-gradient-brand hover:shadow-[0_18px_36px_-12px_rgba(255,45,120,0.55)]",
        // Primary CTA with the brand gradient + glow (used sparingly, e.g. cookie banner)
        gradient:
          "bg-gradient-brand text-white shadow-pill hover:shadow-[0_18px_36px_-12px_rgba(255,45,120,0.55)]",
        // Secondary CTA - dark navy on white
        secondary:
          "bg-primary text-primary-foreground hover:bg-primary/90",
        // Secondary CTA - white with navy border
        outline:
          "border border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground",
        // Secondary CTA on dark sections - white outline
        outlineLight:
          "border border-white/30 bg-transparent text-white hover:bg-white hover:text-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ghost:
          "text-foreground/80 hover:bg-secondary hover:text-foreground",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2 text-[13px]",
        sm: "h-9 px-4 text-[12px]",
        lg: "h-12 px-8 text-[13px]",
        xl: "h-14 px-9 text-[13px]",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
